import type { AdminGymDetail } from '@steado/admin-contract/gyms';
import { useQuery } from '@tanstack/react-query';

import { TabSkeleton } from '@/components/panel/panels';
import { RecordHeader } from '@/components/panel/RecordHeader';
import { BadgeCell } from '@/components/table/cells';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDate, formatRelative } from '@/lib/format';
import { TabError } from '@/screens/user-detail/TabError';

import { AiLimitsPanel } from './AiLimitsPanel';
import { ActivityPanel, CoachingPanel, OwnerPanel, PeoplePanel, TrainersPanel } from './GymPanels';
import { gymDetailQueryOptions } from './gym-detail-query';
import { MemberLimitPanel } from './MemberLimitPanel';

interface GymDetailScreenProps {
  readonly gymId: string;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase() || '??';
}

/**
 * One gym: its owner, its members against its member limit, and what it
 * serves. Read only, except that an admin sets the member limit and the AI
 * limits, and sends the owner's invite again while they have not accepted it. The members themselves are
 * on Users (the People panel links there); this page never names them.
 */
export function GymDetailScreen({ gymId }: GymDetailScreenProps) {
  const { api } = useAuth();
  const query = useQuery(gymDetailQueryOptions(api, gymId));

  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }
  if (query.isPending) {
    return (
      <div className="flex max-w-[1160px] flex-col gap-3">
        <TabSkeleton rows={2} />
        <TabSkeleton rows={6} />
      </div>
    );
  }

  const gym = query.data;
  return (
    <div className="flex max-w-[1160px] flex-col gap-3">
      <GymHeader gym={gym} />
      <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
        <div className="flex flex-col gap-3">
          <PeoplePanel gym={gym} />
          <CoachingPanel gym={gym} />
          <ActivityPanel gym={gym} />
        </div>
        <div className="flex flex-col gap-3">
          <OwnerPanel gym={gym} />
          <MemberLimitPanel gym={gym} />
          <AiLimitsPanel gymId={gym.id} />
        </div>
      </div>
      <TrainersPanel gym={gym} />
    </div>
  );
}

function GymHeader({ gym }: { readonly gym: AdminGymDetail }) {
  const { counts } = gym;
  return (
    <RecordHeader
      label="Gym summary"
      title={gym.name}
      initials={initialsOf(gym.name)}
      badges={
        <>
          {gym.memberCount >= gym.memberLimit ? <BadgeCell label="at member limit" tone="warn" /> : null}
          {gym.setupStep === 'done' ? null : <BadgeCell label={`setup: ${gym.setupStep}`} tone="warn" />}
        </>
      }
      meta={[
        gym.timeZoneLabel,
        `Created ${formatDate(gym.createdAt)}`,
        gym.owner.status === 'invited'
          ? 'Owner invited, not signed in yet'
          : gym.owner.lastSeenAt === null
            ? 'Owner not seen yet'
            : `Owner last seen ${formatRelative(gym.owner.lastSeenAt)}`,
      ]}
      stats={[
        {
          label: 'Members',
          value: `${String(gym.memberCount)}/${String(gym.memberLimit)}`,
          note: placesLeft(gym.memberLimit - gym.memberCount),
        },
        {
          label: 'Trainers',
          value: String(counts.trainersActive),
          note: `${String(counts.trainersInvited)} invited`,
        },
        {
          label: 'Logged in 7 days',
          value: `${String(counts.loggedInWindow)}/${String(counts.coached)}`,
          note: 'a meal or a finished workout',
        },
        {
          label: 'Plans sent 7 days',
          value: String(counts.plansPublishedInWindow),
          note: `${String(counts.plansPublished)} in all`,
        },
      ]}
    />
  );
}

/** "2 places left" under the member limit; "At the limit" when none are. */
function placesLeft(left: number): string {
  if (left <= 0) return 'At the limit';
  return left === 1 ? '1 place left' : `${String(left)} places left`;
}
